<template>
  <div class="facilities-table">
    <div class="facilities-table__row facilities-table__row--heading">
      <div class="facilities-table__cell">{{ $t(facilities.city) }}</div>
      <div
        v-for="level in selectedLevelsBySubscriptionModel"
        :key="level.id"
        class="facilities-table__cell"
      >
        {{ level.value }}
      </div>
    </div>
    <template v-for="object in facilities.objects" :key="object.id">
      <div
        class="facilities-table__row"
        @click="
          isMobileScreen()
            ? openFacilityModal(object.id)
            : openFacilityServices(object)
        "
      >
        <div class="facilities-table__cell">
          <div class="facilities-table__wrapper">
            <div class="facilities-table__text-icon">
              <BaseIcon
                icon="arrow"
                :class="{
                  active: facilityServices?.id === object.id,
                }"
              />
              <p>{{ object.name }}</p>
            </div>
            <IconButton
              icon="circle-info"
              class="facilities-table__object-button"
              @click.stop="openFacilityModal(object.id)"
            />
          </div>
        </div>
        <div
          v-for="level in selectedLevelsBySubscriptionModel"
          :key="level.id"
          class="facilities-table__cell"
        >
          <BaseIcon
            :icon="fallBackIcon"
            :icon-with-description="isLevelIncludedInFacilityLevels(level.id, object.levels)"
          />
        </div>
      </div>
      <template v-if="facilityServices?.id === object.id">
        <div
          v-for="service in object.services"
          class="facilities-table__row facilities-table__row--inner"
        >
          <div class="facilities-table__cell">
            <div class="facilities-table__wrapper">
              <div class="facilities-table__text-icon">
                <BaseIcon icon="line" />
                <p>{{ service.name }}</p>
              </div>
            </div>
          </div>
          <div
            v-for="level in selectedLevelsBySubscriptionModel"
            :key="level.id"
            class="facilities-table__cell"
          >
            <BaseIcon
              :icon="fallBackIcon"
              :icon-with-description="isServiceIncludedInLevel(
                level.id,
                service.name,
                facilityServices.levels
              )"
            />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import IconButton from "~/components/button/IconButton.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import {
  Facility,
  PreparedSupplier,
  ILevel,
  LimitationTypeEnum,
} from "~/server/types/server.interface";
import {useTableFilterStore} from "~/stores/tableFilter";
import {storeToRefs} from "pinia";

const { t } = useI18n();

const iconVariant = {
  "circle-check": "circle-check",
  "circle-x-mark": "circle-x-mark",
  partialLimited: "partialLimited",
  infinity: "infinity",
  badgePlus: "badgePlus",
} as const;

type IconVariantKeys = keyof typeof iconVariant;
type FacilitiesTableProps = {
  facilities: Facility;
};

defineProps<FacilitiesTableProps>();
const emit = defineEmits<{
  selectFacility: [facilityId: string | number];
}>();


const tableFilterStore = useTableFilterStore();
const { selectedLevelsBySubscriptionModel } = storeToRefs(tableFilterStore);

const openFacilityModal = (id: string | number): void => {
  emit("selectFacility", id);
};

const facilityServices = ref<PreparedSupplier | null>(null);
const openFacilityServices = (object: PreparedSupplier): void => {
  if (facilityServices.value?.id === object.id) {
    facilityServices.value = null;
    return;
  }

  facilityServices.value = object;
};

const fallBackIcon = iconVariant["circle-x-mark"];
const isLevelIncludedInFacilityLevels = (
  levelId: string,
  levels: ILevel[]
): { icon: IconVariantKeys, description: string } => {
  const currentLevel = levels.find((level) => level.id === levelId);

  switch (currentLevel?.limitationType) {
  case LimitationTypeEnum.NONE:
    return { icon: iconVariant["infinity"], description: t("facilities_table.infinity-icon") };
  case LimitationTypeEnum.PARTIAL:
    return { icon: iconVariant["partialLimited"], description: t("facilities_table.partial-limited-icon") };
  case LimitationTypeEnum.FULL:
    return { icon: iconVariant["badgePlus"], description: t("facilities_table.badge-plus-icon") };
  default:
    return { icon: iconVariant["circle-x-mark"], description: t("facilities_table.circle-x-mark-icon") };
  }
};

const isServiceIncludedInLevel = (
  levelId: string,
  serviceName: string,
  levels: ILevel[]
): { icon: IconVariantKeys, description: string } => {
  const currentLevel = levels.find((level) => level.id === levelId);
  const currentService = currentLevel && currentLevel.availableServices.find(({ name }) => name === serviceName);

  if (currentService) {
    return currentService.isLimited
      ? { icon: iconVariant["badgePlus"], description: t("facilities_table.badge-plus-icon") }
      : { icon: iconVariant["infinity"], description: t("facilities_table.infinity-icon") };
  }

  return { icon: iconVariant["circle-x-mark"], description: t("facilities_table.circle-x-mark-icon") };
};

const isMobileScreen = (): boolean => {
  return window.matchMedia("(max-width: 719px)").matches;
};
</script>
